import {FC, useState} from 'react';
import {Box, Drawer, Typography, useMediaQuery} from '@mui/material';
import Button from '@components/Button';
import Loader from '@/shared/components/Loader';
import Modal from '@/shared/components/Modal';
import {IMAGE_TABLE_ALT, ERROR, H5} from '@/utils/constants';
import {useAppDispatch, useAppSelector} from '@/store/hooks';
import {
  changeProfile,
  getOrganizationIsLoadingSelector,
  getOrganizationSelector,
  getTeamMemberInfo,
  getTeamSelector,
} from '@/store/organization';
import {ReactComponent as DangerIcon} from '@icons/danger.svg';
import organizationIcon from '@images/organization-example.png';
import ProfileForm from './ProfileForm';
import ChangePasswordModal from './ChangePasswordModal';
import ChangePhotoModal from './ChangePhotoModal';
import {profileContactListStyle} from './ProfileGeneral.style';
import {OrganizationService, OrganizationTeamService, orgStorage} from '@/utils/services';
import {ROUTER_URL, STORAGE_KEY} from '@/utils/enums';
import ProfileTeamMemberGeneral from './ProfileTeamMemberGeneral';
import {toast} from 'react-toastify';
import {getErrorMessage} from '@/utils/helpers';
import {useNavigate} from 'react-router-dom';
import {deleteTeamMember} from '@/store/orgatization-team';
import { useApiInstance } from '@/contexts/ApiInstanceContext';

const ProfileGeneral: FC = () => {
  const [isShowDeleteAvatarModal, setIsShowDeleteAvatarModal] = useState(false);
  const [isShowDeleteAccountModal, setIsShowDeleteAccountModal] = useState(false);
  const [isShowChangePasswordModal, setIsShowChangePasswordModal] = useState(false);
  const [isShowChangePhotoModal, setIsShowChangePhotoModal] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const isLoading = useAppSelector(getOrganizationIsLoadingSelector);
  const organization = useAppSelector(getOrganizationSelector);
  const team = useAppSelector(getTeamSelector);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {apiInstance} = useApiInstance();

  const organizationRole = orgStorage.get(STORAGE_KEY.ORGANIZATION_ROLE);

  const handleDeleteAvatarModalChange = () => setIsShowDeleteAvatarModal(!isShowDeleteAvatarModal);
  const handleDeleteAccountModalChange = () => setIsShowDeleteAccountModal(!isShowDeleteAccountModal);
  const handleChangePasswordModalChange = () => setIsShowChangePasswordModal(!isShowChangePasswordModal);
  const handleChangePhotoModalChange = () => setIsShowChangePhotoModal(!isShowChangePhotoModal);
  const handleOpenPopup = () => {
    if (!isMobile) return;
    setIsPopupOpen(!isPopupOpen);
  };

  const deleteAvatarOrganization = async () => {
    if (!organization) {
      return;
    }
    const {id, name, address, email, taxIdNumber, phoneNumber, city, state, zip} = organization;

    const params = {
      id,
      name,
      address,
      email,
      taxIdNumber,
      phoneNumber,
      logo: null,
      city,
      state,
      zip,
    };

    try {
      await dispatch(changeProfile(params)).unwrap();
      handleDeleteAvatarModalChange();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const deleteAvatarTeam = async () => {
    if (!team) {
      return;
    }
    const {id, firstName, lastName, email} = team;

    const params = {
      id,
      firstName,
      lastName,
      email,
      logo: null,
    };

    try {
      await OrganizationTeamService.updateTeamMember(team.id as number, params);
      await dispatch(getTeamMemberInfo(team?.id as number)).unwrap();
      handleDeleteAvatarModalChange();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const deleteOrganization = async () => {
    if (!organization) {
      return;
    }
    try {
      await OrganizationService.deleteOrganization(organization.id);
      handleDeleteAccountModalChange();
      orgStorage.clear();
      navigate(`/${ROUTER_URL.LOGIN}`);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };
  const deleteTeam = async () => {
    if (!team) {
      return;
    }

    try {
      await dispatch(deleteTeamMember(team.id)).unwrap();
      handleDeleteAccountModalChange();
      orgStorage.clear();
      navigate(`/${ROUTER_URL.LOGIN}`);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const roleGeneralProfile: Record<string, JSX.Element | null> = {
    Owner: organization && <ProfileForm organization={organization} />,
    Admin: team && <ProfileTeamMemberGeneral team={team} />,
    Standart: team && <ProfileTeamMemberGeneral team={team} />,
  };

  const isMobile = useMediaQuery('(max-width:640px)');

  return (
    <Box sx={profileContactListStyle.wrapper}>
      {isLoading ? (
        <Box sx={profileContactListStyle.loaderWrap}>
          <Loader size={50} />
        </Box>
      ) : (
        <>
          <Box sx={profileContactListStyle.avatarContainer}>
            <Box sx={profileContactListStyle.avatarImage} onClick={handleOpenPopup}>
              <img
                src={
                  organizationRole === 'Owner' && !!organization?.logo
                    ? organization.logo
                    : team && !!team?.logo
                    ? team.logo
                    : organizationIcon
                }
                alt={IMAGE_TABLE_ALT}
              />
            </Box>

            {!isMobile && (
              <Box>
                <Button
                  sx={profileContactListStyle.btn}
                  color="error"
                  variant="text"
                  onClick={handleDeleteAvatarModalChange}
                  title="Remove Photo"
                  disabled={!organization?.logo && !team?.logo}
                />

                <Button
                  sx={profileContactListStyle.btn}
                  variant="text"
                  onClick={handleChangePhotoModalChange}
                  title="Change Photo"
                />
              </Box>
            )}
          </Box>
          {roleGeneralProfile[organizationRole as string] ?? null}
        </>
      )}

      {!isMobile && (
        <Box sx={profileContactListStyle.widget}>
          <Box>
            <Typography variant={H5} sx={profileContactListStyle.widgetTitle}>
              Password
            </Typography>
            <Typography sx={profileContactListStyle.secondaryText}>Update your password at any time.</Typography>
          </Box>
          <Box>
            <Button
              variant="text"
              sx={profileContactListStyle.btn}
              onClick={handleChangePasswordModalChange}
              title="Change Password"
            />
          </Box>
        </Box>
      )}
      <Box sx={profileContactListStyle.widget}>
        <Box>
          <Typography variant={H5} sx={profileContactListStyle.widgetTitle}>
            Delete Account
          </Typography>
          <Typography sx={profileContactListStyle.secondaryText}>
            Permanently delete your account and all associated data. This action cannot be undone.
          </Typography>
        </Box>
        <Box>
          <Button
            color="error"
            variant="text"
            sx={[profileContactListStyle.btn, profileContactListStyle.red]}
            onClick={handleDeleteAccountModalChange}
            title="Delete account"
          />
        </Box>

        <Drawer anchor="bottom" open={isPopupOpen} onClose={handleOpenPopup}>
          <Box>
            <Typography sx={{p: 2, fontWeight: 600, fontSize: 16}}>Organization Logo</Typography>
            <Box sx={profileContactListStyle.popupBox}>
              <Button title="Change Photo" variant="text" onClick={handleChangePhotoModalChange} />
              <Button title="Delete Photo" variant="text" onClick={handleDeleteAvatarModalChange} />
              <Button title="Cancel" variant="text" onClick={handleOpenPopup} />
            </Box>
          </Box>
        </Drawer>
      </Box>

      <Modal
        closeButtonTitle="Cancel"
        mainButtonTitle="Delete"
        onClose={handleDeleteAccountModalChange}
        onSave={organizationRole === 'Owner' ? deleteOrganization : deleteTeam}
        open={isShowDeleteAccountModal}
        sx={profileContactListStyle.modal}
        title="Delete Account"
        description="Are you sure you want to delete?"
        mainIcon={<DangerIcon />}
        color={ERROR}
        isShowCloseButton
        isShowSaveButton
      />
      <Modal
        closeButtonTitle="Cancel"
        mainButtonTitle="Delete"
        onClose={handleDeleteAvatarModalChange}
        onSave={organizationRole === 'Owner' ? deleteAvatarOrganization : deleteAvatarTeam}
        open={isShowDeleteAvatarModal}
        sx={profileContactListStyle.modal}
        title="Delete Photo"
        description="Are you sure you want to delete?"
        mainIcon={<DangerIcon />}
        color={ERROR}
        isShowCloseButton
        isShowSaveButton
      />

      <Modal
        onClose={handleChangePasswordModalChange}
        open={isShowChangePasswordModal}
        sx={profileContactListStyle.modal}
      >
        <ChangePasswordModal onCloseModal={handleChangePasswordModalChange} apiInstance={apiInstance} />
      </Modal>
      <Modal
        onClose={handleChangePhotoModalChange}
        open={isShowChangePhotoModal}
        sx={profileContactListStyle.customeModal}
      >
        <ChangePhotoModal onCloseModal={handleChangePhotoModalChange} />
      </Modal>
    </Box>
  );
};

export default ProfileGeneral;
